<template>
  <div ref="chart" class="echarts-retention-heatmap" role="img" :aria-label="ariaLabel" />
</template>

<script>
import echarts, { chartAnimationDuration } from "./echarts-core"

const monthFormatter = new Intl.DateTimeFormat("en-CA", { month: "short", year: "numeric", timeZone: "UTC" })
const percentFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 1 })

export default {
  name: "EChartsRetentionHeatmap",
  props: {
    cohorts: { type: Array, required: true },
    periods: { type: Array, required: true },
    isDark: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "Monthly customer cohort retention heatmap" },
  },
  data() { return { chart: null, resizeObserver: null } },
  mounted() { this.$nextTick(this.initChart) },
  beforeDestroy() {
    window.removeEventListener("resize", this.refresh)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.chart) this.chart.dispose()
  },
  watch: {
    cohorts: { handler() { this.updateChart() }, deep: true },
    periods: { handler() { this.updateChart() }, deep: true },
    isDark() { this.updateChart() },
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart, null, { renderer: "canvas" })
      window.addEventListener("resize", this.refresh)
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
      this.updateChart()
    },
    formatMonth(month) {
      return monthFormatter.format(new Date(`${month}-01T00:00:00.000Z`))
    },
    updateChart() {
      if (!this.chart) return this.$nextTick(this.initChart)
      const textColor = this.isDark ? "rgba(255,255,255,0.74)" : "rgba(0,0,0,0.64)"
      const borderColor = this.isDark ? "#232323" : "#ffffff"
      const data = this.cohorts.flatMap((cohort, rowIndex) => cohort.cells.map(cell => ({
        value: [Number(cell.offset), rowIndex, Number(cell.rate || 0)],
        customers: Number(cell.customers || 0),
        cohortSize: Number(cohort.cohortSize || 0),
        cohortMonth: cohort.cohortMonth,
      })))
      this.chart.setOption({
        animationDuration: chartAnimationDuration(),
        grid: { top: 12, right: 20, bottom: 62, left: 18, containLabel: true },
        tooltip: {
          position: "top",
          renderMode: "richText",
          confine: true,
          backgroundColor: this.isDark ? "rgba(35,35,35,0.96)" : "rgba(20,20,20,0.9)",
          borderWidth: 0,
          textStyle: { color: "#fff", fontSize: 12 },
          formatter: point => point?.data ? [
            `${this.formatMonth(point.data.cohortMonth)} cohort · M${point.data.value[0]}`,
            `${percentFormatter.format(point.data.value[2])}% retained`,
            `${point.data.customers} of ${point.data.cohortSize} customers`,
          ].join("\n") : "",
        },
        xAxis: {
          type: "category",
          data: this.periods.map(offset => `M${offset}`),
          name: "Months since first purchase",
          nameLocation: "middle",
          nameGap: 34,
          nameTextStyle: { color: textColor, fontSize: 10 },
          axisLabel: { color: textColor, fontSize: 11 },
          axisLine: { show: false },
          axisTick: { show: false },
          splitArea: { show: true },
        },
        yAxis: {
          type: "category",
          data: this.cohorts.map(cohort => `${this.formatMonth(cohort.cohortMonth)} · ${cohort.cohortSize}`),
          inverse: true,
          axisLabel: { color: textColor, fontSize: 11 },
          axisLine: { show: false },
          axisTick: { show: false },
          splitArea: { show: true },
        },
        visualMap: {
          min: 0,
          max: 100,
          calculable: false,
          orient: "horizontal",
          left: "center",
          bottom: 0,
          text: ["100%", "0%"],
          textStyle: { color: textColor, fontSize: 10 },
          inRange: { color: this.isDark ? ["#2b2420", "#7a633d", "#4ade80"] : ["#f4ede4", "#d8b979", "#1f6b57"] },
        },
        series: [{
          name: "Retention",
          type: "heatmap",
          data,
          label: {
            show: true,
            color: this.isDark ? "#fff" : "#0d2d2c",
            fontSize: 10,
            fontWeight: 700,
            formatter: point => `${percentFormatter.format(point.value[2])}%`,
          },
          itemStyle: { borderColor, borderWidth: 2, borderRadius: 3 },
          emphasis: { itemStyle: { shadowBlur: 8, shadowColor: "rgba(0,0,0,0.22)" } },
        }],
      }, true)
      this.refresh()
    },
    refresh() { this.$nextTick(() => this.chart?.resize()) },
  },
}
</script>

<style scoped>
.echarts-retention-heatmap { height: 100%; min-height: 300px; width: 100%; }
</style>
